import { ReactNode, useEffect, useState } from "react";
import { UseFormRegisterReturn } from "react-hook-form";

import { getCategories, groupByParent } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import { Category } from "../../types/category";
import { useTranslation } from "../../core/translation.core";

export default function CategoryInput({ register }: { register: () => UseFormRegisterReturn }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { t, msg } = useTranslation();

  useEffect(() => {
    getCategories().then((cats) => {
      setCategories(cats);
    });
  }, []);

  const groupedCats = groupByParent(categories);
  const filteredCats = filterCategories(groupedCats, searchTerm);

  return (
    <div id="cat-selector" className="border rounded-2xl max-h-[500px] overflow-y-scroll p-4">
      <div className="mb-4">
        <input
          type="text"
          placeholder={t(msg.category.search)}
          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-her-backblue dark:border-white text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div id="cat-selector-list">{renderRows(filteredCats, register)}</div>
    </div>
  );
}

function filterCategories(cats: Category[], searchTerm: string): Category[] {
  if (!searchTerm) return cats;

  const matchesSearch = (cat: Category): boolean => {
    const name = localeCore.getLocaleValueDefaultFallback(cat.name)?.toLowerCase() || "";
    return name.includes(searchTerm.toLowerCase());
  };

  const filterChildrenAndCheckMatches = (cat: Category): { filteredChildren: Category[]; hasMatch: boolean } => {
    if (!cat.children?.length) {
      return { filteredChildren: [], hasMatch: matchesSearch(cat) };
    }

    const selfMatches = matchesSearch(cat);
    const childResults = cat.children.map((child) => filterChildrenAndCheckMatches(child));

    const matchingChildren = cat.children
      .filter((child, index) => {
        return childResults[index].hasMatch || matchesSearch(child);
      })
      .map((child, index) => ({
        ...child,
        children: childResults[index].filteredChildren,
      }));

    return {
      filteredChildren: matchingChildren,
      hasMatch: selfMatches || matchingChildren.length > 0,
    };
  };

  return cats
    .map((cat) => {
      const result = filterChildrenAndCheckMatches(cat);
      if (result.hasMatch) {
        return {
          ...cat,
          children: result.filteredChildren,
        };
      }
      return null;
    })
    .filter((cat): cat is Category => cat !== null);
}

function renderIndent(indent: number): ReactNode[] {
  var list: ReactNode[] = [];
  for (var i = 0; i < indent; i++) {
    list.push(<span key={i} className="ml-4"></span>);
  }
  return list;
}

function Row({ category, indent = 0, register }: { category: Category; indent: number; register: () => UseFormRegisterReturn }) {
  return (
    <div>
      <input type="checkbox" id={`cat_${category.id}`} {...register()} value={category.id} />
      <label htmlFor={`cat_${category.id}`} className="pl-4" data-catcode={category.code}>
        {renderIndent(indent)}
        <span>{localeCore.getLocaleValueDefaultFallback(category.name)}</span>
      </label>
    </div>
  );
}

function renderRows(groupedCats: Category[], register: () => UseFormRegisterReturn) {
  var rows: ReactNode[] = [];

  for (const groupedCat of groupedCats) {
    addRowsRecursive(0, rows, groupedCat, register);
  }

  return rows;
}

function addRowsRecursive(level: number, returnList: ReactNode[], current: Category, register: () => UseFormRegisterReturn) {
  returnList.push(<Row key={current.id} indent={level} category={current} register={register} />);

  if (current.children !== undefined && current.children.length > 0) {
    for (var c of current.children) {
      addRowsRecursive(level + 1, returnList, c, register);
    }
  }
}
